import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useNotifications, useMarkAllRead } from "../../hooks/useNotifications";
import { NotificationRow } from "../../components/community/notifications/NotificationRow";
import { SeenBatcherProvider } from "../../components/community/notifications/SeenBatcher";
import { CheckCheck, Settings, Loader2, BellRing } from "lucide-react";
import { trackEvent } from "../../utils/analytics";

function NotificationsPage() {
  const [filter, setFilter] = useState("all"); // 'all', 'unread', 'approvals', 'engagement', 'credits'

  useEffect(() => {
    trackEvent("notifications_page_viewed", {});
  }, []);

  // We use the unreadOnly API param only if the 'unread' filter is selected to optimize fetching.
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError } = useNotifications({ 
    unreadOnly: filter === "unread",
    isAuthenticated: true 
  });
  
  const { mutate: markAllRead } = useMarkAllRead();

  // Flatten the infinite pages array
  const allNotifications = data?.pages.flatMap((page) => page.data) || [];

  // Client-side filtering
  const filteredNotifications = allNotifications.filter((n) => {
    if (filter === "all") return true;
    if (filter === "unread") return true; // Already filtered by API
    
    if (filter === "approvals") {
      return n.type === "POST_APPROVED" || n.type === "POST_REJECTED";
    }
    if (filter === "engagement") {
      return ["POST_LIKED", "POST_SAVED", "POST_COPIED", "COMMENT_RECEIVED", "NEW_FOLLOWER"].includes(n.type);
    }
    if (filter === "credits") {
      return n.type === "CREDIT_EARNED" || n.type === "BADGE_AWARDED";
    }
    return true;
  });

  // Client-side grouping by relative date
  const groupNotifications = (notifications) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);
    const monthAgo = new Date(today);
    monthAgo.setMonth(monthAgo.getMonth() - 1);

    const groups = {
      Today: [],
      Yesterday: [],
      "This Week": [],
      "This Month": [],
      Older: []
    };

    notifications.forEach((n) => {
      const date = new Date(n.createdAt);
      if (date >= today) {
        groups.Today.push(n);
      } else if (date >= yesterday) {
        groups.Yesterday.push(n);
      } else if (date >= weekAgo) {
        groups["This Week"].push(n);
      } else if (date >= monthAgo) {
        groups["This Month"].push(n);
      } else {
        groups.Older.push(n);
      }
    });

    return groups;
  };

  const grouped = groupNotifications(filteredNotifications);
  const hasAnyItems = filteredNotifications.length > 0;

  const filters = [
    { id: "all", label: "All" },
    { id: "unread", label: "Unread" },
    // Renamed for auto-publish: nothing waits for approval, but takedowns still notify.
    { id: "approvals", label: "Post Status" },
    { id: "engagement", label: "Engagement" },
    { id: "credits", label: "RoamPoints & Badges" },
  ];

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight flex items-center gap-3">
              Notifications
            </h1>
            <p className="text-gray-500 mt-1">Stay updated on your community interactions.</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                trackEvent("notifications_marked_all_read", { source: "page" });
                markAllRead();
              }}
              className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2 shadow-sm"
            >
              <CheckCheck size={16} />
              Mark all read
            </button>
            <Link 
              to="/settings/notifications" 
              className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
              aria-label="Notification Settings"
            >
              <Settings size={18} />
            </Link>
          </div>
        </div>

        {/* Filters */}
        <div className="flex overflow-x-auto scrollbar-hidden gap-2 mb-6 pb-2">
          {filters.map((f) => (
            <button
              key={f.id}
              onClick={() => {
                trackEvent("notification_filter_changed", { filter: f.id });
                setFilter(f.id);
              }}
              className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                filter === f.id 
                  ? "bg-gray-900 text-white" 
                  : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden min-h-[400px]">
          {isLoading && (
            <div className="flex flex-col items-center justify-center p-20 text-gray-400">
              <Loader2 className="animate-spin w-8 h-8 mb-4 text-indigo-500" />
              <p>Loading notifications...</p>
            </div>
          )}

          {isError && (
            <div className="p-20 text-center text-red-500 bg-red-50/50">
              <p className="font-semibold mb-2">Couldn't load notifications</p>
              <p className="text-sm">Please check your connection and try again.</p>
            </div>
          )}

          {!isLoading && !isError && !hasAnyItems && (
            <div className="flex flex-col items-center justify-center p-24 text-center">
              <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                {filter === "unread" ? (
                  <CheckCheck className="w-8 h-8 text-gray-300" />
                ) : (
                  <BellRing className="w-8 h-8 text-gray-300" />
                )}
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">
                {filter === "unread" ? "No unread notifications" : "You're all caught up"}
              </h3>
              <p className="text-gray-500">
                {filter === "all" || filter === "unread" 
                  ? "New activity on your posts will appear here." 
                  : "No notifications found for this category."}
              </p>
            </div>
          )}

          {!isLoading && !isError && hasAnyItems && (
            <div className="flex flex-col">
              {Object.entries(grouped).map(([groupName, items]) => {
                if (items.length === 0) return null;
                return (
                  <div key={groupName}>
                    <div className="bg-gray-50/80 px-5 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider sticky top-0 z-10 border-b border-gray-100 border-t border-t-transparent first:border-t-0">
                      {groupName}
                    </div>
                    <div>
                      {items.map((notif) => (
                        <NotificationRow key={notif.id} notification={notif} />
                      ))}
                    </div>
                  </div>
                );
              })}

              {/* Load More */}
              {hasNextPage && (
                <div className="p-4 border-t border-gray-100">
                  <button
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                    className="w-full py-3 rounded-lg bg-gray-50 text-indigo-600 font-semibold hover:bg-indigo-50 transition-colors disabled:opacity-50 flex justify-center items-center gap-2"
                  >
                    {isFetchingNextPage && <Loader2 className="w-4 h-4 animate-spin" />}
                    {isFetchingNextPage ? "Loading older notifications..." : "Load more notifications"}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Wrapped so rows on this page batch their seen-reports the same way the
// dropdown's do, instead of firing one write per row while the user scrolls.
export default function Notifications() {
  return (
    <SeenBatcherProvider>
      <NotificationsPage />
    </SeenBatcherProvider>
  );
}
